import {
  Box,
  Chip,
  Paper,
  Stack,
  alpha,
  Button,
  useTheme,
  Typography,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import { renderFormattedDate } from 'utils';
import RefreshIcon from '@mui/icons-material/Refresh';
import { FormattedMessage, useIntl } from 'react-intl';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';

type ReportHeaderProps = {
  loading: boolean;
  loadedAt: string | null;
  onRefresh: () => void;
};

const ReportHeader = ({ loading, loadedAt, onRefresh }: ReportHeaderProps) => {
  const intl = useIntl();
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        mb: 2,
        overflow: 'hidden',
        position: 'relative',
        border: '1px solid',
        borderColor: 'divider',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08)} 0%, ${alpha(
          theme.palette.success.main,
          theme.palette.mode === 'dark' ? 0.08 : 0.035
        )} 100%)`,
      }}>
      {loading && <LinearProgress sx={{ position: 'absolute', inset: '0 0 auto' }} />}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} justifyContent="space-between">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 54,
              height: 54,
              display: 'grid',
              flexShrink: 0,
              placeItems: 'center',
              color: 'primary.main',
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
              '& .MuiSvgIcon-root': { fontSize: 30 },
            }}>
            <InsightsRoundedIcon />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
              <Typography component="h1" variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                <FormattedMessage id="freshdeskFeedback.title" />
              </Typography>
              <Chip
                size="small"
                icon={<AdminPanelSettingsOutlinedIcon />}
                label={<FormattedMessage id="freshdeskFeedback.managerView" />}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 700 }}
              />
            </Stack>

            <Typography color="text.secondary" sx={{ maxWidth: 760 }}>
              <FormattedMessage id="freshdeskFeedback.subtitle" />
            </Typography>

            <Chip
              size="small"
              icon={<AccessTimeRoundedIcon />}
              label={
                loadedAt
                  ? intl.formatMessage(
                      { id: 'freshdeskFeedback.lastUpdated' },
                      { date: renderFormattedDate(loadedAt, true) }
                    )
                  : intl.formatMessage({ id: 'freshdeskFeedback.notUpdatedYet' })
              }
              sx={{ mt: 1.5, fontWeight: 600 }}
            />
          </Box>
        </Stack>

        <Button
          variant="contained"
          onClick={onRefresh}
          disabled={loading}
          startIcon={loading ? <CircularProgress color="inherit" size={18} /> : <RefreshIcon />}
          sx={{ alignSelf: { xs: 'stretch', md: 'center' }, flexShrink: 0 }}>
          <FormattedMessage id="freshdeskFeedback.refresh" />
        </Button>
      </Stack>
    </Paper>
  );
};

export default ReportHeader;
